import React, { useEffect } from "react"
import Markdown from "react-markdown"

import { Heading } from "@/components/heading"
import CodeBlock from "@/components/ui/code-block"
import { CopyPasta } from "@/components/ui/copy-pasta"
import {
  Example as ExampleData,
  ExampleStep,
  ExampleStepBase,
  InstallDependenciesStep,
  RustCargoDepMap,
  ShellCommandStep,
  StepType,
  TemplateVariables,
  TextStep,
  WorkDirStep,
  WriteFileStep,
  applyTemplates,
} from "@/examples/examples"

export type ExampleProps = {
  example: ExampleData
  variables?: TemplateVariables
}

export function Example({ example, variables }: ExampleProps) {
  const [templatedExample, setTemplatedExample] = React.useState(example)
  useEffect(() => {
    ;(async () => {
      const newExample = await applyTemplates(variables || {}, example)
      setTemplatedExample(newExample)
    })()
  }, [example])

  return (
    <div className="my-4">
      <header className="mb-8 border-b border-muted-foreground/10">
        <Heading level={2}>{templatedExample.title}</Heading>
      </header>
      {templatedExample.steps.map((step, i) => (
        <Step
          key={i}
          index={i}
          step={step}
          showIndex={templatedExample.steps.length > 1}
        />
      ))}
    </div>
  )
}

interface ExampleStepProps {
  step: ExampleStepBase
  index: number
  showIndex: boolean
}

export function Step({ index, step, showIndex }: ExampleStepProps) {
  if (showIndex) {
    return (
      <div className="ml-2 pb-7 relative border-l-2 box-border border-slate-300 dark:border-slate-800">
        <div className="absolute top-0 -left-3 w-5.5 h-5.5 rounded-full bg-slate-600 dark:bg-slate-400">
          <span className="absolute text-white dark:text-slate-900 text-sm inset-0 text-center font-bold">
            {index + 1}
          </span>
        </div>
        {step.title && (
          <Heading level={4} className="ml-5 -mt-2">
            {step.title}
          </Heading>
        )}
        <div className="ml-5">
          <StepInner index={index} step={step} showIndex={showIndex} />
        </div>
      </div>
    )
  } else {
    return (
      <div>
        {step.title && (
          <Heading level={4} className="text-sm">
            {step.title}
          </Heading>
        )}
        <StepInner index={index} step={step} showIndex={showIndex} />
      </div>
    )
  }
}

function StepInner({ index, step, showIndex }: ExampleStepProps) {
  switch (step.type) {
    case StepType.Text:
      return (
        <Text step={step as TextStep} index={index} showIndex={showIndex} />
      )
    case StepType.ShellCommand:
      return <ShellCommand step={step as ShellCommandStep} />
    case StepType.InstallDependencies:
      return <InstallDependencies step={step as InstallDependenciesStep} />
    case StepType.WriteFile:
      return <WriteFile step={step as WriteFileStep} />
    default:
      return null
  }
}

function Text({
  step,
  index,
  showIndex,
}: { step: TextStep; index: number; showIndex: boolean }) {
  return (
    <div>
      <Markdown>{step.description}</Markdown>
      {step.steps && (
        <div>
          {step.steps.map((subStep, j) => (
            <StepInner
              key={index + j * 0.1}
              index={index + j * 0.1}
              step={subStep}
              showIndex={showIndex}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ShellCommand({ step }: { step: ShellCommandStep }) {
  const text = Array.isArray(step.rust) ? step.rust.join("\n") : step.rust
  return (
    <div>
      {step.description && <Markdown>{step.description}</Markdown>}
      <CopyPasta text={text || ""} />
    </div>
  )
}

function InstallDependencies({ step }: { step: InstallDependenciesStep }) {
  return (
    <div>
      <p>
        Make sure your <span>Cargo.toml</span> has these dependencies included:
      </p>
      <CodeBlock
        language="toml"
        filename="cargo.toml"
        text={cargoDepsString(step.rust || {})}
      />
    </div>
  )
}

// renders the rust dependencies section of a Cargo.toml file
// from a ProtocolInfo object
function cargoDepsString(dependencies: RustCargoDepMap): string {
  const deps = Object.keys(dependencies).map(
    (crate) => `${crate} = ${toToml(dependencies[crate])}`,
  )
  return ["[dependencies]", ...deps].join("\n")
}

function toToml(obj: any): string {
  switch (typeof obj) {
    case "string":
      return `"${obj}"`
    case "number":
      return obj.toString()
    case "boolean":
      return obj.toString()
    case "object":
      if (Array.isArray(obj)) {
        return `[${obj.map(toToml).join(", ")}]`
      } else {
        const kv = Object.entries(obj)
          .map(([k, v]) => `${k} = ${toToml(v)}`)
          .join(", ")
        return `{ ${kv} }`
      }
  }
  throw new Error(`encountered unsupported type ${typeof obj}`)
}

function WriteFile({ step }: { step: WriteFileStep }) {
  return (
    <div>
      {step.description && <Markdown>{step.description}</Markdown>}
      <CodeBlock
        language="rust"
        filename={step.rust?.path}
        text={step.rust?.content || ""}
      />
    </div>
  )
}
